import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  PRODUCT_NAME_PATTERN,
  PRODUCT_BRAND_PATTERN,
  PRODUCT_UPC_PATTERN,
  PRODUCT_PRICE,
  PRODUCT_DESCRIPTION_PATTERN,
} from '../inventory/validators/inventory-form.validator';
import { TranslatePipe } from '@ngx-translate/core';
import { EditInventorySectionComponent } from './components/edit-inventory-section/edit-inventory-section.component';
import { EditInventoryUiService } from './services/edit-inventory-ui.service';
import { IconName } from '@app/utils/enums/icon-enum';
import {
  InventoryItemFullInformation,
  InventoryProductType,
  UpdateInventoryItem,
} from '@app/utils/interfaces/inventory-item.interface';
import {
  EDIT_INVENTORY_TABS,
  EditInventoryTab,
  EMPTY_INVENTORY_ITEM,
} from '@app/utils/constants/inventory-table.constant';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { AppRoutes } from '@app/utils/enums/routes.enum';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { InventoryTabKeys } from '@app/utils/enums/inventory-table-columns.enum';
import { ProductDetailFormControlsType } from '@app/utils/interfaces/product-detail-form-controls.interface';
import { InventoryService } from '@services/inventory/inventory.service';
import { catchError, EMPTY, tap } from 'rxjs';

@Component({
  selector: 'app-edit-inventory-item-page',
  standalone: true,
  imports: [MatProgressSpinnerModule, TranslatePipe, IconComponent, EditInventorySectionComponent],
  templateUrl: './edit-inventory-item-page.component.html',
  styleUrl: './edit-inventory-item-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditInventoryItemPageComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);
  private readonly editSection = viewChild.required(EditInventorySectionComponent);
  private readonly uiService = inject(EditInventoryUiService);
  private readonly inventoryService = inject(InventoryService);

  protected inventoryItem = input.required<InventoryItemFullInformation>();
  protected isLoading = signal(false);
  protected productType = signal<InventoryProductType>('single');
  protected isHeaderCollapsed = this.uiService.isHeaderCollapsed;
  protected activeTab = this.uiService.activeTab;

  public readonly iconName = IconName;
  public readonly tabs = EDIT_INVENTORY_TABS;
  public form!: FormGroup;

  public ngOnInit(): void {
    this.initForm();
    this.setupProductTypeListener();
    this.patchFormValues();
  }

  public readonly visibleTabs = computed(() => {
    const type = this.productType();

    return this.tabs.filter((tab) => {
      if (type === 'variants') {
        return tab.key !== InventoryTabKeys.price && tab.key !== InventoryTabKeys.availability;
      } else {
        return tab.key !== InventoryTabKeys.variants;
      }
    });
  });

  public onTabClick(tab: EditInventoryTab): void {
    this.activeTab.set(tab.key);
    this.editSection().scrollToSection(tab.key);
  }

  public onBack(): void {
    this.navigateToInventory();
  }

  public onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    const item = this.inventoryItem();
    const formValues = this.form.getRawValue();

    const formPrice = Number(formValues.priceForm?.price ?? item?.price ?? 0);
    const formIsPromoted =
      formValues.priceForm?.applyPromotion !== undefined
        ? !!formValues.priceForm?.applyPromotion
        : !!item?.isPromoted;

    const stockForm = formValues.availabilityForm;
    const updatedOnHand = stockForm ? Number(stockForm.onHand) : Number(item?.onHandAmount || 0);
    const updatedReserved = stockForm ? Number(stockForm.reserved) : Number(item?.reservedAmount || 0);
    const updatedAvailable = Math.max(0, updatedOnHand - updatedReserved);
    let formPromotionPrice = Number(
      formValues.priceForm?.promotionValue ?? item?.promotionPrice ?? 0,
    );
    if (!formIsPromoted) {
      formPromotionPrice = formPrice > 1 ? formPrice - 1 : 0;
    }

    const updatePayload: UpdateInventoryItem = {
      productName: formValues.productName,
      brandId: item?.brand?.id || 0,
      categoryId: Number(formValues.productCategory),
      productUpc: String(formValues.productUpc),
      description: formValues.productDescription,
      isActive: formValues.isActive,

      onHandAmount: updatedOnHand,
      reservedAmount: updatedReserved,
      availableAmount: updatedAvailable,

      price: formPrice,
      promotionPrice: formPromotionPrice,
      isPromoted: formIsPromoted,
    };

    this.inventoryService
      .updateInventoryItem(item.id, updatePayload)
      .pipe(
        catchError(() => {
          this.isLoading.set(false);
          this.openErrorModal(
            'inventory.apiErrors.serverErrorTitle',
            'inventory.apiErrors.updateInventoryItemInfo',
          );

          return EMPTY;
        }),
        tap(() => {
          this.isLoading.set(false);
          this.inventoryService.notifyRefresh();
          this.navigateToInventory();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private setupProductTypeListener(): void {
    this.form
      .get('productType')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: InventoryProductType) => {
        this.productType.set(value);

        const currentTab = this.activeTab();
        const isCurrentTabVisible = this.visibleTabs().some((tab) => tab.key === currentTab);

        if (!isCurrentTabVisible) {
          this.activeTab.set(InventoryTabKeys.general);
        }
      });
  }

  private initForm(): void {
    this.form = this.fb.group({
      productType: [EMPTY_INVENTORY_ITEM.hasVariations],
      productName: ['', [Validators.required, Validators.pattern(PRODUCT_NAME_PATTERN)]],
      productBrand: ['', [Validators.required, Validators.pattern(PRODUCT_BRAND_PATTERN)]],
      productUpc: ['', [Validators.required, Validators.pattern(PRODUCT_UPC_PATTERN)]],
      productCategory: [null, Validators.required],
      priceUsd: ['', [Validators.required, Validators.pattern(PRODUCT_PRICE)]],
      productDescription: [
        '',
        [Validators.required, Validators.pattern(PRODUCT_DESCRIPTION_PATTERN)],
      ],
      isActive: [true],
      productDetails: this.fb.array<FormGroup<ProductDetailFormControlsType>>([]),
    });
  }

  private patchFormValues(): void {
    const item = this.inventoryItem();

    if (!item) return;
    const currentStatus = item.status;

    const isCurrentlyActive =
      currentStatus === 'Available' ||
      currentStatus === 'Committed' ||
      currentStatus === 'Incoming' ||
      currentStatus === 'New';

    this.form.patchValue({
      productType: item.hasVariations ? 'variants' : 'single',
      productName: item.productName,
      productUpc: item.productUpc,
      productBrand: item.brand?.name || '',
      priceUsd: item.price,
      productCategory: item.mainCategory?.id ?? null,
      productDescription: item.description,
      isActive: isCurrentlyActive,
    });

    const isUnmodifiable = currentStatus === 'Committed' || currentStatus === 'Incoming';

    if (isUnmodifiable) {
      this.form.get('isActive')?.disable();
    } else {
      this.form.get('isActive')?.enable();
    }
  }

  protected getProductDetails(): FormArray<FormGroup<ProductDetailFormControlsType>> {
    return this.form.get('productDetails') as FormArray<FormGroup<ProductDetailFormControlsType>>;
  }

  private navigateToInventory(): void {
    this.router.navigate([AppRoutes.INVENTORY_LIST]);
  }

  private resetTabs(): void {
    this.uiService.setActiveTab(InventoryTabKeys.general);
    this.uiService.setHeaderCollapsed(false);
  }

  private openErrorModal(
    title = 'inventory.apiErrors.serverErrorTitle',
    description = 'inventory.apiErrors.getInventoryItemInfo',
  ): void {
    this.dialog.open(StatusModalComponent, {
      data: {
        isSuccess: false,
        title,
        description,
      },
    });
  }
}
